import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Home, Search, ArrowLeft, Music, User } from 'lucide-react';
import { motion } from 'framer-motion';

// Official STAGE mascot image
const STAGE_IMAGE = "https://customer-assets.emergentagent.com/job_dmv-media-1/artifacts/4l63gu1k_C18FA1E7-13BE-4A8D-B43C-DA825C418C9F.png";

export default function NotFoundPage() {
  const location = useLocation();
  const navigate = useNavigate();

  // Check if this is an /@username route and redirect to /u/username
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/@')) {
      const handle = path.slice(2); // Remove '/@'
      if (handle && handle !== 'undefined' && handle !== 'null') {
        console.log('[NotFoundPage] Redirecting @' + handle + ' to /u/' + handle);
        navigate(`/u/${handle}`, { replace: true });
        return;
      }
    }
  }, [location.pathname, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" data-testid="not-found-page">
      <div className="max-w-lg w-full text-center">
        {/* STAGE Mascot */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, type: "spring" }}
          className="mb-8"
        >
          <img 
            src={STAGE_IMAGE} 
            alt="STAGE mascot" 
            className="w-40 h-40 mx-auto object-contain"
          />
        </motion.div>

        {/* 404 Text */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <h1 className="font-heading text-8xl font-bold text-secondary mb-2">404</h1>
          <h2 className="font-heading text-2xl font-bold uppercase mb-4">
            This Stage is Empty
          </h2>
          <p className="text-muted-foreground mb-8 max-w-sm mx-auto">
            Looks like this page took a wrong turn. Don&apos;t worry, STAGE will help you find your way back to the music.
          </p>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="flex flex-col sm:flex-row gap-3 justify-center"
        >
          <Link to="/">
            <Button className="bg-secondary hover:bg-secondary/90 w-full sm:w-auto">
              <Home className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
          <Link to="/select-role">
            <Button variant="outline" className="border-secondary/40 w-full sm:w-auto">
              <User className="w-4 h-4 mr-2" />
              Join / Sign Up
            </Button>
          </Link>
          <Link to="/charts">
            <Button variant="outline" className="border-border w-full sm:w-auto">
              <Music className="w-4 h-4 mr-2" />
              View Charts
            </Button>
          </Link>
        </motion.div>

        {/* Go Back Link */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-8"
        >
          <button 
            onClick={() => window.history.back()}
            className="text-sm text-muted-foreground hover:text-secondary transition-colors flex items-center gap-2 mx-auto"
          >
            <ArrowLeft className="w-4 h-4" />
            Go back to previous page
          </button>
        </motion.div>

        {/* Footer decoration */}
        <div className="mt-12 flex justify-center gap-2">
          <div className="w-2 h-2 rounded-full bg-primary/50" />
          <div className="w-2 h-2 rounded-full bg-secondary/50" />
          <div className="w-2 h-2 rounded-full bg-primary/50" />
        </div>
      </div>
    </div>
  );
}
